import { faCalendar, faCaretDown, faCaretUp, faKeyboard, faXmark } from "@fortawesome/free-solid-svg-icons";
import If from "components/common/If";
import React, { createRef, useContext } from "react"
import i18n from 'config/translations/i18n';
import { BaseInputPropsType, InputErrorType } from "types/form-types";
import { validateInput } from "utilities/input-utilities"
import { getTranslationText } from "utilities/translation-utilities"
import '../../Form.scss';
import InputErrors from "../input-common/InputErrors";
import InputExtension from "../input-common/InputExtension"
import InputLabel from "../input-common/InputLabel";
import Calendar from "components/common/calendar/Calendar";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import { FormContextManager, getFormContext } from "config/FormContext";
import { dateToString } from "utilities/calendar-utilities";
import "./DateInput.scss";

type DateInputPropsType = Omit<BaseInputPropsType<HTMLInputElement, Date>, 'onChange'> & {
    onChange?: (s: Date | undefined, event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | React.ChangeEvent) => void
    onBlur?: (s: Date | undefined, event: React.MouseEvent | React.KeyboardEvent) => void,
    min?: Date
    max?: Date,
    disabledDates?: Date[],
    viewDate?: Date
    highlightDates?: Date[] | null,
    format?: DateFormats,
    toDay?: boolean
}

type DateInputPropsTypeInternal = DateInputPropsType & {
    formContext: FormContextManager
}

type DateInputStateType = {
    touched: boolean
    errors: InputErrorType[],
    inputValue: string,
    data?: Date,
    opened: boolean,
    loading: boolean,
    isFocused: boolean,
    keyboardActive: boolean,
}

class DateInputClass extends React.Component<DateInputPropsTypeInternal, DateInputStateType> {
    private readonly t = i18n.t;
    private readonly input = createRef<HTMLInputElement>();
    private readonly inputWrapper = createRef<HTMLDivElement>();
    private readonly dropdownWrapper = createRef<HTMLDivElement>();
    private readonly placeholder: string | null | undefined;
    private readonly popoverTargetName;
    constructor(props: DateInputPropsTypeInternal) {
        super(props)
        this.state = {
            touched: props.touched || props.value ? true : false,
            errors: [],
            data: undefined,
            inputValue: this.format(this.props.value) || '',
            loading: false,
            opened: false,
            isFocused: false,
            keyboardActive: false,
        }
        this.placeholder = getTranslationText(this.props.placeholder, this.t) || " dd/MM/yyyy";
        this.popoverTargetName = `${this.props.name}-date-input-calendar-popover-target`;
    }

    componentDidMount(): void {
        document.addEventListener('click', this.onFocusOut);
        this.validate(!this.state.touched);
    }

    componentDidUpdate(prevProps: Readonly<DateInputPropsType>): void {
        if (prevProps.value != this.props.value) {
            this.setState({
                touched: true,
                inputValue: this.format(this.props.value) || ''
            })
            this.validate(true);
        }
    }

    componentWillUnmount(): void {
        document.removeEventListener('click', this.onFocusOut);
    }

    private onInputValueChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (this.props.disabled || this.props.readonly) return;
        let val = event.target.value;
        let selectStartIndex = 0;

        if (val && (event.nativeEvent as InputEvent).inputType !== 'deleteContentBackward') {
            const toDayDate = this.props.value || new Date();
            selectStartIndex = val.length + 1;
            if (/^[0-9]{2}$/.test(val)) {
                val += `/${String(toDayDate.getMonth() + 1).padStart(2, '0')}/${toDayDate.getFullYear()}`;
            } else if (/^[0-9]{2}\/[0-9]{2}$/.test(val)) {
                val += `/${toDayDate.getFullYear()}`;
            }
        }

        this.setState({
            inputValue: val
        });

        if (DateUtil.isValid(val)) {
            this.onChange(DateUtil.ToDate(val, DateFormats.LONG), event);
        }

        if (selectStartIndex > 0) {
            setTimeout(() => {
                if (this.input.current) {
                    this.input.current.setSelectionRange(selectStartIndex, val.length);
                }
            }, 100);
        }
    }

    private onInputFocus = async (event: React.FocusEvent<HTMLInputElement>) => {
        if (this.props.disabled || this.props.readonly) {
            this.setState({
                opened: false,
                isFocused: true
            });
            return;
        }

        event.target.select();
        this.setOpened(true, { touched: true });
    }

    private onFocusOut = (e: MouseEvent | KeyboardEvent | FocusEvent) => {
        if (this.props.disabled || this.props.readonly) {
            this.setState({
                opened: false
            });
            return;
        }
        if (this.inputWrapper.current && this.inputWrapper.current.contains(e.target as Node)) {
            return;
        }

        this.setOpened(false, { inputValue: this.format(this.props.value) });
        this.validate(!this.state.touched);
    }

    private format(date: Date | undefined) {
        if (!date) return '';
        return DateUtil.format(date)
    }

    private setOpened = (open: boolean, state?: Partial<DateInputStateType>) => {
        if (this.props.disabled || this.props.readonly) {
            this.setState({
                opened: false
            });
            return;
        }
        this.setState({
            ...this.state,
            opened: open,
            ...state
        });

        if (open) {
            console.log('showing popover');
            if (!this.dropdownWrapper.current?.matches(':popover-open')) {
                this.dropdownWrapper.current?.showPopover();
            }
        }
        else {
            console.log('hiding popover');
            if (this.dropdownWrapper.current?.matches(':popover-open')) {
                this.dropdownWrapper.current?.hidePopover();
            }
        }

        if (!open && this.state.touched) this.validate(this.state.touched);
    }

    private onKeyPress = (e: React.KeyboardEvent) => {
        if (this.props.disabled || this.props.readonly) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this.setOpened(false);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            this.setOpened(!this.state.opened);
        } else if (e.key === 'Tab') {
            this.setOpened(false);
        } else if (e.key === 'ArrowDown') {
            this.setOpened(true);
        } else if (e.key === 'ArrowUp') {
            this.setOpened(true);
        }
    }

    private showTodayButton = () => {
        if (this.props.min && dateToString(this.props.min) > dateToString(new Date())) {
            return false;
        }
        if (this.props.max && dateToString(this.props.max) < dateToString(new Date())) {
            return false;
        }
        if (this.props.disabledDates && !this.props.disabledDates.some(d => dateToString(d) === dateToString(new Date()))) {
            return false;
        }

        return true;
    }

    // #region value

    private onClear = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        this.onChange(undefined, event);
    }

    private onChange = (i: Date | undefined, event: React.MouseEvent | React.KeyboardEvent | React.ChangeEvent) => {
        if (typeof this.props.onChange == 'function') {
            this.props.onChange(i, event);
        }

        this.setOpened(false);
    };

    private validate = (soft?: boolean) => {
        if (this.state.touched) soft = false;
        const customErrors = [...(this.props.customErrors || [])];
        if (this.props.required) {
            customErrors.push({
                name: 'valueMissing',
                message: 'form.errors.valueMissing',
                validate: () => {
                    return this.props.value ? true : false;
                },
            })
        }

        if (this.props.min) {
            customErrors.push({
                name: 'rangeUnderflow',
                message: 'form.errors.rangeUnderflow',
                validate: () => {
                    if (!this.props.value) return true;
                    return dateToString(this.props.value) >= dateToString(this.props.min!) ? true : false;
                },
            })
        }

        if (this.props.max) {
            customErrors.push({
                name: 'rangeOverflow',
                message: 'form.errors.rangeOverflow',
                validate: () => {
                    if (!this.props.value) return true;
                    return dateToString(this.props.value) <= dateToString(this.props.max!) ? true : false;
                },
            })
        }

        if (this.props.disabledDates?.length) {
            customErrors.push({
                name: 'disabledDates',
                message: 'form.errors.disabledDates',
                validate: () => {
                    if (!this.props.value) return true;
                    return this.props.disabledDates?.find(d => dateToString(d) === dateToString(this.props.value!)) ? false : true;
                },
            })
        }

        customErrors.push({
            name: 'invalidFormat',
            message: 'form.errors.patternMismatch',
            validate: () => {
                if (!this.props.value) return true;
                return DateUtil.isValid(this.props.value) ? true : false;
            },
        })

        const errors = validateInput({
            value: this.props.value,
            input: this.input.current!,
            inputWrapper: this.inputWrapper.current!,
            customErrors: customErrors,
            soft: soft ? true : false
        });

        this.setState({
            errors: errors || []
        })
    }
    // #endregion value

    // #region render
    render(): React.ReactNode {
        return (
            <div
                ref={this.inputWrapper}
                className={`popup-anchor-calendar app-input-wrapper dropdown-input relative box-border ${this.props.disabled ? 'disabled' : ''} ${this.props.readonly ? 'readonly' : ''}`.trim()}
                popoverTarget={this.popoverTargetName}
            >
                <label>
                    <InputLabel label={this.props.label} required={this.props.required} />
                    <div
                        className="input-container flex"
                    >
                        <InputExtension isFocused={this.state.isFocused} icons={this.props.prefix} />
                        <input
                            ref={this.input}
                            type="text"
                            name={this.props.name}
                            autoFocus={this.props.autoFocus}
                            size={50}
                            onFocus={this.onInputFocus}
                            className={`${this.props.prefix ? '' : 'pl'} ${this.props.suffix ? '' : 'pr'}`.trim()}
                            onKeyUp={this.onKeyPress}
                            value={this.state.inputValue}
                            inputMode={this.state.keyboardActive ? undefined : 'none'}
                            onBlur={() => this.setState({ isFocused: false })}
                            onChange={this.onInputValueChange}
                            placeholder={`${this.placeholder || ''}${this.props.required ? ' *' : ''}`}
                        />
                        <InputExtension key='dropdown-clear' icons={{ icon: faXmark, name: 'clear', hidden: () => !this.props.required && this.props.value ? false : true, onClick: this.onClear }} />
                        <InputExtension icons={this.props.suffix} isFocused={this.state.isFocused} />
                        <If condition={this.showTodayButton()}>
                            <InputExtension key='dropdown-today' icons={{ icon: faCalendar, name: 'today', onClick: e => this.onChange(new Date(), e) }} />
                        </If>
                        <InputExtension
                            key='dropdown-open'
                            icons={{
                                icon: faCaretDown, name: 'open', hidden: () => this.state.opened, onClick: (e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    this.setOpened(true)
                                }
                            }}
                        />
                        <InputExtension
                            key='dropdown-close'
                            icons={{
                                icon: faCaretUp, name: 'close', hidden: () => !this.state.opened, onClick: (e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    this.setOpened(false)
                                }
                            }}
                        />
                    </div>
                </label>
                <InputErrors errors={this.state.errors} />
                <div popover="manual" className="min-w-[330px] border border-color shadow-xl popup-anchor-calendar-dropdown" ref={this.dropdownWrapper} id={this.popoverTargetName}>
                    <Calendar
                        name={`${this.props.name + '-date-input-calendar'}`}
                        onDateSelected={this.onChange}
                        selectedDate={this.props.value}
                        maxDate={this.props.max}
                        minDate={this.props.min}
                        viewDate={this.props.viewDate && new Date(this.props.viewDate)}
                        highlightDates={this.props.highlightDates}
                        disabledDates={this.props.disabledDates}
                    />
                </div>
            </div>
        );
    }
    // #endregion render
};

function DateInput(props: DateInputPropsType) {
    const formContext = getFormContext(useContext);
    return <DateInputClass
        {...props}
        formContext={formContext}
    />
}

export default DateInput;
